import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router';

import { Semaphore } from '@dr.pogodin/react-utils';
import { addBreadcrumb } from '@sentry/react';

import Throbber from 'components/Throbber';
import { usePlayback } from 'components/ai-app/Playback';
import { useExperience, useSession } from 'hooks';
import { finishSession } from 'services/session';

import { MODALITY_COLORS, formatMinsAsMMSS, useGlobalStateObject } from 'utils';

import type { VideoT } from 'utils/video';

import AudioSettings from './AudioSettings';
import ChapterTimeline, { type ChapterT } from './ChapterTimeline';
import FullscreenButton from './FullscreenButton';
import SpeedPill from './SpeedPill';
import TopBar from './TopBar';
import Transport from './Transport';
import { useAutoHide } from './useAutoHide';
import { useOrientation } from './useOrientation';

import S from './style.scss';

// A loosely-typed view of ScreenOrientation; `lock` is not in lib.dom, and
// `orientation` itself is absent on older browsers (hence `| undefined`).
type LockableOrientationT = {
  lock?: (orientation: 'landscape') => Promise<void>;
  unlock?: () => void;
};

// Both helpers run only from client event handlers / effects, never during SSR.
function lockLandscape(): void {
  try {
    const o = window.screen.orientation as unknown as
      LockableOrientationT | undefined;
    const p = o?.lock?.('landscape');
    if (p) p.catch(() => undefined);
  } catch { /* unsupported (e.g. iOS Safari) — ignore. */ }
}

function unlockOrientation(): void {
  try {
    const o = window.screen.orientation as unknown as
      LockableOrientationT | undefined;
    o?.unlock?.();
  } catch { /* unsupported — ignore. */ }
}

type HeapT = {
  experienceDurationMin: number;
  finalize?: () => Promise<void>;
  finalizing: boolean;
  muted?: boolean;
  paused?: boolean;

  /** `defer` prepares + holds the segment (paused) instead of playing it. */
  play: (idx: number, pos: number, defer?: boolean) => Promise<void> | void;

  /** These fields synchronize and abort overlapping play() operations. */
  playOpIdx: number;
  playbackRate: number;
  sem: Semaphore;
  video: VideoT;
  videoIdx: number;
  videos?: VideoT[];
};

type SessionStateT = {
  muted: boolean;
  paused: boolean;
  playbackRate: number;
  playerVisible: boolean;

  /** The overall progress through the session, a number within the [0,1] range. */
  sessionProgress: number;

  throbber: boolean;
  video: VideoT;
  videoIdx: number;

  /** The progress through the current video, a number within the [0,1] range. */
  videoProgress: number;
};

/** Builds the chapter descriptors (start fraction, thumbnail, etc.) for the
 *  session scrubber from the experience's ordered videos. */
function buildChapters(videos: VideoT[]): ChapterT[] {
  let total = 0;
  for (const v of videos) total += v.durationMin ?? 0;

  const chapters: ChapterT[] = [];
  let acc = 0;
  for (let i = 0; i < videos.length; ++i) {
    const v = videos[i];
    if (!v) throw Error('Missing video');

    const [firstModality] = v.modalities;
    chapters.push({
      borderColor: firstModality ? MODALITY_COLORS[firstModality] : undefined,
      id: `${i}/${v.url}`,
      start: total ? acc / total : 0,
      thumbnailUrl: v.thumbnailUrl,
      title: v.title ?? '',
    });
    acc += v.durationMin ?? 0;
  }
  return chapters;
}

/** Resolves once the element has loaded metadata (or errors out). */
async function waitForMetadata(v: HTMLVideoElement): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      v.removeEventListener('loadedmetadata', done);
      v.removeEventListener('error', done);
      resolve();
    };
    v.addEventListener('loadedmetadata', done, { once: true });
    v.addEventListener('error', done, { once: true });
  });
}

/**
 * Seeks the element to `pos` (a [0,1] fraction of its duration) and resumes
 * playback (unless paused), re-applying rate + muted. Rechecks the generation
 * guard around the metadata await so a newer transition cancels this one.
 * With `hold`, it prepares the segment (seek + rate + muted) but leaves it
 * paused, so a caller can reveal it later when a transition fade-out begins.
 */
async function seekAndPlay(
  video: HTMLVideoElement,
  pos: number,
  playOpIdx: number,
  heap: HeapT,
  setState: (updater: (prev: SessionStateT) => SessionStateT) => void,
  hold = false,
): Promise<void> {
  const v = video;

  // Seeks need real metadata; video.duration is NaN until it loads.
  if (!Number.isFinite(v.duration) || !v.duration) await waitForMetadata(v);
  if (playOpIdx !== heap.playOpIdx) return;

  if (v.error) {
    if (playOpIdx === heap.playOpIdx) {
      setState((prev) => ({ ...prev, throbber: false }));
    }
    return;
  }

  if (Number.isFinite(v.duration) && v.duration) {
    v.currentTime = pos * v.duration;
  }
  v.playbackRate = heap.playbackRate;
  v.muted = Boolean(heap.muted);

  if (hold || heap.paused) v.pause();
  else {
    await v.play().catch((e: unknown) => {
      // AbortError = play() interrupted by a newer load(); expected, ignore.
      if (e instanceof Error && e.name === 'NotSupportedError') {
        // Unplayable source (e.g. expired link); the "error" listener retries -
        // log it so the stall isn't silent.
        addBreadcrumb({
          category: 'session-video',
          level: 'warning',
          message: `seekAndPlay NotSupportedError: ${v.currentSrc}`,
        });
      } else if (e instanceof Error && e.name !== 'AbortError') throw e;
    });
  }

  if (playOpIdx === heap.playOpIdx) {
    setState((prev) => ({ ...prev, throbber: false }));
  }
}

const Session: FunctionComponent = () => {
  const experience = useExperience();

  const experienceData = experience.data?.experience;
  if (!experienceData?.videos.length) throw Error('Empty experience');

  const {
    endTransition, getVideo, mountVideo, playTransition,
    refresh, releaseVideo, sourceFor, transitioning,
  } = usePlayback();

  const [firstVideo] = experienceData.videos;
  if (!firstVideo) throw Error('Missing first video');

  const [state, setState] = useState<SessionStateT>({
    muted: false,
    paused: false,
    playbackRate: 1,
    playerVisible: false,
    sessionProgress: 0,
    throbber: true,
    video: firstVideo,
    videoIdx: 0,
    videoProgress: 0,
  });

  // UI-only flags kept out of SessionStateT so the high-frequency timeupdate
  // updates above don't churn them.
  const [fullscreen, setFullscreen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const onAudioOpenChange = useCallback((next: boolean) => {
    setAudioOpen(next);
  }, []);
  const [speedOpen, setSpeedOpen] = useState(false);
  const onSpeedOpenChange = useCallback((next: boolean) => {
    setSpeedOpen(next);
  }, []);
  const { reveal, visible } = useAutoHide({
    hold: audioOpen || speedOpen,
    paused: state.paused,
    throbber: state.throbber,
  });
  const landscape = useOrientation();

  const heapRef = useRef<HeapT>({
    experienceDurationMin: 0,
    finalizing: false,
    play: () => undefined,
    playOpIdx: 0,
    playbackRate: 1,
    sem: new Semaphore(true),
    video: firstVideo,
    videoIdx: 0,
  });

  // TODO: Yeah... we need some refactoring if ESLint forbids such pattern now.
  // eslint-disable-next-line react-hooks/refs
  const heap = heapRef.current;

  heap.muted = state.muted;
  heap.paused = state.paused;
  heap.videos = experienceData.videos;

  const navigate = useNavigate();

  const session = useSession();
  if (!session.data) throw Error('No active session');

  const gs = useGlobalStateObject();

  heap.finalize = async () => {
    if (!heap.finalizing) {
      heap.finalizing = true;
      try {
        if (!session.data) throw Error('Missing session data');
        const finished = await finishSession(session.data);

        const past = gs.get('pastSessions.data.data');
        gs.set('pastSessions.data.data', past ? [finished, ...past] : [finished]);

        session.set(finished);

        await navigate('/post-session-evaluation');
        // The overlay lives above the routes, so it survives this navigation —
        // fade it out to reveal the evaluation page (the outro bookend).
        endTransition();
      } catch (e) {
        // Finish failed and we stayed on the page — don't strand the outro logo.
        endTransition();
        throw e;
      } finally {
        // eslint-disable-next-line require-atomic-updates
        heap.finalizing = false;
      }
    }
  };

  const playerContainerRef = useRef<HTMLDivElement>(null);

  // TODO: This should be stored in the experience object itself,
  // perhaps calculated by Mongoose as a virtual field. For our current
  // purposes we assume it is a constant stored in heap, that's why it is
  // fine to use it around this component as a part of the global or local
  // state.
  heap.experienceDurationMin = 0;
  for (const { durationMin } of experienceData.videos) {
    // TODO: Bad, refactor.
    // eslint-disable-next-line react-hooks/refs
    heap.experienceDurationMin += durationMin ?? 0;
  }

  /**
   * Moves playback to the specified position of the target video.
   * @param idx Index of the target video within the current experience.
   * @param pos Start position inside the target video, a number within
   *  the [0,1] range.
   */
  // TODO: Bad, refactor.
  // eslint-disable-next-line react-hooks/refs
  heap.play = async (idx: number, pos: number, defer = false) => {
    const video = experienceData.videos[idx];
    if (!video) throw Error('Bad video index');

    // Update the position in local state right away, so the UI feels responsive.
    let sessionProgress = 0;
    for (let i = 0; i < idx; ++i) {
      const v = experienceData.videos[i];
      if (!v) throw Error('Missing video');
      sessionProgress += (v.durationMin ?? 0) / heap.experienceDurationMin;
    }
    sessionProgress += pos * (video.durationMin ?? 0)
      / heap.experienceDurationMin;

    setState((prev) => ({
      ...prev,
      sessionProgress,
      throbber: true,
      video,
      videoIdx: idx,
      videoProgress: pos,
    }));

    // Serialize play() operations with a generation guard, so a newer call
    // aborts an in-flight one (and swallows the resulting AbortError below).
    if (++heap.playOpIdx === Number.MAX_SAFE_INTEGER) heap.playOpIdx = 0;
    const { playOpIdx } = heap;

    try {
      await heap.sem.seize();
      if (playOpIdx !== heap.playOpIdx) return;

      const v = getVideo();
      if (!v) return;

      // Pick the source for the target segment; re-mint it if missing or if the
      // element is in an error state (e.g. an expired signed link).
      let src = sourceFor(video.url);
      if (!src || v.error) src = await refresh(video.url);
      if (playOpIdx !== heap.playOpIdx) return;
      if (!src) {
        // No playable source — stop the throbber rather than spin forever.
        setState((prev) => ({ ...prev, throbber: false }));
        return;
      }

      const sameSegment = idx === heap.videoIdx && !v.error;
      if (!sameSegment || v.currentSrc !== src) {
        v.pause();
        setState((prev) => ({ ...prev, playerVisible: false }));
        v.src = src;
        v.load();
      }
      heap.video = video;
      heap.videoIdx = idx;

      await seekAndPlay(v, pos, playOpIdx, heap, setState, defer);
      if (defer && playOpIdx === heap.playOpIdx) {
        // Segment is prepared and held at `pos`. Start the fade-out now, and
        // resume it (from its start) the moment that fade-out begins.
        endTransition(() => void heap.play(idx, pos));
      }
    } finally {
      heap.sem.setReady(true);
    }
  };

  // Adopt the shared <video> on mount (it arrives unlocked and playing segment 0
  // from the "Start Your Session" tap), and release it on unmount.
  useEffect(() => {
    const heap2 = heapRef.current;
    const v = getVideo();
    const container = playerContainerRef.current;
    if (!v || !container) return undefined;

    mountVideo(container);

    const onPlaying = (): void => {
      setState((prev) => ({ ...prev, playerVisible: true, throbber: false }));
      // The new segment is rendering — fade the masking logo back out.
      endTransition();
    };

    const onTimeUpdate = (): void => {
      if (!heap2.sem.ready) return;
      if (!Number.isFinite(v.duration) || !v.duration) return;
      const videoProgress = v.currentTime / v.duration;
      const dur = heap2.video.durationMin ?? 0;
      setState((prev) => ({
        ...prev,
        sessionProgress: prev.sessionProgress
          + (videoProgress - prev.videoProgress) * dur
          / heap2.experienceDurationMin,
        videoProgress,
      }));
    };

    const onEnded = (): void => {
      if (v.error) return;
      // Fade the logo in, then prepare-and-hold the next clip (defer) so it's
      // revealed from its start when the fade-out begins — instead of playing
      // under the opaque logo. The last clip finalizes (logo masks the nav).
      playTransition();
      if (!heap2.videos) throw Error('Missing videos');
      if (heap2.videoIdx < heap2.videos.length - 1) {
        void heap2.play(heap2.videoIdx + 1, 0, true);
      } else {
        if (!heap2.finalize) throw Error('Missing finalize() callback.');
        void heap2.finalize();
      }
    };

    const onError = (): void => {
      // Mid-playback error (e.g. an expired link): re-run the current segment,
      // which re-mints the source. Only when not already in a play() op.
      if (heap2.sem.ready) void heap2.play(heap2.videoIdx, 0);
    };

    v.addEventListener('playing', onPlaying);
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('ended', onEnded);
    v.addEventListener('error', onError);

    void (async () => {
      await heap2.sem.seize();
      const { playOpIdx } = heap2;
      try {
        const url = heap2.videos?.[0]?.url;
        if (!url) throw Error('Missing URL');

        const src0 = sourceFor(url) ?? await refresh(url);
        if (playOpIdx !== heap2.playOpIdx) return;
        if (src0 && v.currentSrc !== src0) {
          v.src = src0;
          v.load();
        }
        v.playbackRate = heap2.playbackRate;
        // Don't start segment 0 here. While the intro logo is up it stays held
        // at the start (paused by the unlock gesture). Kick off the fade-out and
        // resume it (from its start) the moment that fade-out begins.
        endTransition(() => void heap2.play(0, 0));
      } finally {
        heap2.sem.setReady(true);
      }
    })();

    return () => {
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('error', onError);
      void (async () => {
        await heap2.sem.seize();
        v.pause();
        releaseVideo();
        heap2.sem.setReady(true);
      })();
    };
  }, [
    endTransition,
    getVideo,
    mountVideo,
    playTransition,
    refresh,
    releaseVideo,
    sourceFor,
  ]);

  // On unmount (e.g. on Finish), leave orientation unlocked and reset the shared
  // <video> back to "contain", so neither is stranded for the next session.
  useEffect(() => () => {
    unlockOrientation();
    const v = getVideo();
    if (v) v.style.objectFit = 'contain';
  }, [getVideo]);

  // --- Control handlers (bind to the existing playback engine only) ---------

  const onPlayPause = (): void => {
    void (async () => {
      const paused = !heap.paused;
      heap.paused = paused;
      setState((prev) => ({ ...prev, paused }));
      try {
        await heap.sem.seize();
        const v = getVideo();
        if (!v) return;
        if (paused) v.pause();
        else {
          await v.play().catch((e: unknown) => {
            if (e instanceof Error && e.name === 'NotSupportedError') {
              addBreadcrumb({
                category: 'session-video',
                level: 'warning',
                message: `onPlayPause NotSupportedError: ${v.currentSrc}`,
              });
            } else if (e instanceof Error && e.name !== 'AbortError') throw e;
          });
        }
      } finally {
        heap.sem.setReady(true);
      }
    })();
  };

  const onNext = (): void => {
    void heap.play(state.videoIdx + 1, 0);
  };

  const onPrev = (): void => {
    const v = getVideo();
    const cur = v && Number.isFinite(v.duration) && v.duration
      ? v.currentTime : 0;
    // Past the first few seconds, "previous" restarts the current segment;
    // otherwise it steps back to the previous one (clamped at the first).
    if (cur > 3) void heap.play(state.videoIdx, 0);
    else void heap.play(Math.max(0, state.videoIdx - 1), 0);
  };

  const setRate = (playbackRate: number): void => {
    const v = getVideo();
    if (v) v.playbackRate = playbackRate;
    heap.playbackRate = playbackRate;
    setState((prev) => ({ ...prev, playbackRate }));
  };

  const onToggleCoaching = (): void => {
    const muted = !state.muted;
    const v = getVideo();
    if (v) v.muted = muted;
    heap.muted = muted;
    setState((prev) => ({ ...prev, muted }));
  };

  const onToggleFullscreen = (): void => {
    const next = !fullscreen;
    setFullscreen(next);

    // Fill the viewport (crop the letterbox/pillarbox side bars) when maximized.
    const v = getVideo();
    if (v) v.style.objectFit = next ? 'cover' : 'contain';

    if (next) lockLandscape();
    else unlockOrientation();
  };

  // Seek to an absolute [0,1] fraction of the whole session: find the segment
  // it lands in and the local position within it, then drive playback.
  const onSeek = (fraction: number): void => {
    const vids = experienceData.videos;
    let total = 0;
    for (const v of vids) total += v.durationMin ?? 0;
    const target = fraction * total;
    let acc = 0;
    for (let i = 0; i < vids.length; ++i) {
      const vid = vids[i];
      if (!vid) throw Error('Missing video');

      const dur = vid.durationMin ?? 0;
      if (target <= acc + dur || i === vids.length - 1) {
        const local = dur > 0
          ? Math.min(1, Math.max(0, (target - acc) / dur))
          : 0;
        void heap.play(i, local);
        return;
      }
      acc += dur;
    }
  };

  const onSeekChapter = (idx: number): void => {
    void heap.play(idx, 0);
  };

  const chapters = buildChapters(experienceData.videos);

  const sessionTime = formatMinsAsMMSS(
    // TODO: Yeah... it should be state.
    // eslint-disable-next-line react-hooks/refs
    heap.experienceDurationMin * (1 - state.sessionProgress),
  );

  let containerClassName = S.container;
  if (fullscreen) containerClassName += ` ${S.fullscreen}`;

  let overlayClassName = S.overlay;
  if (!visible) overlayClassName += ` ${S.hidden}`;

  return (
    <div
      className={containerClassName}
      onKeyDown={reveal}
      onPointerMove={reveal}
      role="presentation"
    >
      {state.throbber && !transitioning
        ? <div styleName="throbberContainer"><Throbber withDebounce /></div>
        : null}
      <div
        ref={playerContainerRef}
        style={{ opacity: state.playerVisible ? 1 : 0 }}
        styleName="playerContainer"
      />

      {visible ? null : (
        <div
          className={S.revealCatcher}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            reveal();
          }}
          role="presentation"
        />
      )}

      <div className={overlayClassName}>
        <TopBar
          fullscreen={fullscreen}
          onFinish={() => {
            if (!heap.finalize) throw Error('Missing finalize() callback');
            void heap.finalize();
          }}
          onToggleFullscreen={onToggleFullscreen}
          sessionTime={sessionTime}
          title={state.video.title ?? ''}
        />

        <div styleName="bottom">
          <div styleName="controlsRow">
            <AudioSettings
              coaching={!state.muted}
              onOpenChange={onAudioOpenChange}
              onToggleCoaching={onToggleCoaching}
            />
            <div styleName="transportCenter">
              <Transport
                canNext={state.videoIdx < chapters.length - 1}
                canPrev={state.videoIdx > 0 || state.videoProgress > 0}
                onNext={onNext}
                onPlayPause={onPlayPause}
                onPrev={onPrev}
                paused={state.paused}
              />
            </div>
            <div styleName="rightGroup">
              <SpeedPill
                expanded={landscape}
                onOpenChange={onSpeedOpenChange}
                rate={state.playbackRate}
                setRate={setRate}
              />
              <div styleName="fullscreenLandscape">
                <FullscreenButton
                  active={fullscreen}
                  onToggle={onToggleFullscreen}
                />
              </div>
            </div>
          </div>

          <ChapterTimeline
            activeIdx={state.videoIdx}
            chapters={chapters}
            onSeek={onSeek}
            onSeekChapter={onSeekChapter}
            progress={state.sessionProgress}
            showMarkers={!fullscreen}
          />
        </div>
      </div>
    </div>
  );
};

export default Session;
